import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, AlertTriangle, Building2,
  Download, UploadCloud, FileSpreadsheet, Plus, Trash2, Pencil, HelpCircle,
  Undo2, Link2, ShieldCheck, CheckCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import ReviewedBadge from '../components/ReviewedBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import api from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, shortId } from '../lib/utils';

export default function DepositoDetailPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { payoutId } = useParams();
  const { user } = useAuth();

  const isAllowed = ['admin', 'accountant'].includes(user?.role);
  const [exporting, setExporting] = useState(false);
  const emptyLinkDialog = {
    open: false, orderId: '', docNumber: '', amount: null, reason: null,
    // Fix 64 (2026-10-26): "Cambiar factura" de una línea ya emparejada
    orderIds: [], currentDoc: '',
  };
  const [linkDialog, setLinkDialog] = useState(emptyLinkDialog);
  const closeLinkDialog = () => setLinkDialog(emptyLinkDialog);

  const linkInvoiceMutation = useMutation({
    // Fix 64 (2026-10-26): una línea de factura puede traer varias órdenes;
    // al cambiarla se vinculan todas a la factura nueva (mismo endpoint).
    mutationFn: async ({ orderId, orderIds, docNumber }) => {
      const ids = (orderIds && orderIds.length) ? orderIds : [orderId];
      let last = null;
      for (const id of ids) {
        last = (await api.post(`/deposits/${encodeURIComponent(payoutId)}/link-invoice`,
          { order_id: id, doc_number: docNumber })).data;
      }
      return last;
    },
    onSuccess: (data, vars) => {
      const mismatch = data?.amount_mismatch
        ? t('deposits.link_invoice_amount_mismatch')
          .replace('{{doc}}', vars?.docNumber || '')
          .replace('{{invoice}}', formatCurrency(data.invoice_total, locale))
          .replace('{{order}}', formatCurrency(data.revenue_amount, locale))
        : (data?.warning || null);
      toast.success(mismatch
        ? `${t('deposits.link_invoice_success')} — ${mismatch}`
        : t('deposits.link_invoice_success'), { duration: mismatch ? 12000 : 5000 });
      closeLinkDialog();
      qc.invalidateQueries({ queryKey: ['deposits'] });
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || t('deposits.link_invoice_error'),
        { duration: 10000 });
    },
  });

  const q = useQuery({
    queryKey: ['deposits', 'detail', payoutId],
    queryFn: async () => (await api.get(`/deposits/${payoutId}/preview`)).data,
    enabled: isAllowed && !!payoutId,
    retry: false,
  });

  const accountsQ = useQuery({
    queryKey: ['deposits', 'adjustment-accounts'],
    queryFn: async () => (await api.get('/deposits/adjustment-accounts')).data,
    enabled: isAllowed,
    staleTime: 60_000,
  });
  const eligibleAccounts = accountsQ.data?.accounts || [];

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const res = await api.get('/deposits/export', {
        params: { format: fmt, payout_ids: payoutId },
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `lovi-deposito-${payoutId}.${fmt}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('deposits.export_done'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('deposits.export_error'));
    } finally {
      setExporting(false);
    }
  };

  const [markDialogOpen, setMarkDialogOpen] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, existing_deposits: [] });

  const commitMutation = useMutation({
    mutationFn: async ({ force } = {}) =>
      (await api.post(`/deposits/${payoutId}/commit`, {}, {
        params: force ? { force: true } : undefined,
      })).data,
    onSuccess: (data) => {
      const id = data.deposit_id || '—';
      const mode = data.deposit_mode === 'mock' ? ' (MOCK)' : '';
      toast.success(`${t('deposits.toast_committed')} ${id}${mode}`);
      qc.invalidateQueries({ queryKey: ['deposits'] });
      navigate('/depositos');
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.reason === 'possible_duplicate') {
        setDuplicateDialog({ open: true, existing_deposits: detail.existing_deposits || [] });
        return;
      }
      if (detail && typeof detail === 'object') {
        const qboErr = detail.qbo_error;
        const errMsg = typeof qboErr === 'string'
          ? qboErr
          : JSON.stringify(qboErr, null, 2);
        toast.error(
          <div className="space-y-1">
            <p className="font-semibold">{detail.message || t('deposits.toast_error')}</p>
            {detail.qbo_status && (
              <p className="text-xs font-mono">QBO status: {detail.qbo_status}</p>
            )}
            {qboErr && (
              <pre className="text-xs whitespace-pre-wrap break-all max-h-40 overflow-auto">
                {errMsg}
              </pre>
            )}
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(t('deposits.toast_error'));
      }
    },
  });

  const markMutation = useMutation({
    mutationFn: async ({ note, qboDepositId } = {}) =>
      (await api.post(`/deposits/${payoutId}/mark-reconciled`, {
        note: note || undefined, qbo_deposit_id: qboDepositId || undefined,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      qc.invalidateQueries({ queryKey: ['deposits', 'list'] });
      toast.success(t('deposits.mark_reconciled_success'));
      setMarkDialogOpen(false);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('deposits.mark_reconciled_error'));
    },
  });

  // Estado intermedio "Revisado ✓" — NO cambia el status ni escribe en QBO.
  const reviewMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/deposits/${payoutId}/toggle-review`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      qc.invalidateQueries({ queryKey: ['deposits', 'list'] });
      toast.success(data?.reviewed
        ? t('deposits.review_marked')
        : t('deposits.review_unmarked'));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('deposits.review_error'));
    },
  });

  const unmarkMutation = useMutation({    mutationFn: async () => (await api.post(`/deposits/${payoutId}/unmark-reconciled`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      qc.invalidateQueries({ queryKey: ['deposits', 'list'] });
      toast.success(t('deposits.unmark_reconciled_success'));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('deposits.unmark_reconciled_error'));
    },
  });

  const linkExistingMutation = useMutation({
    mutationFn: async (qboDepositId) =>
      (await api.post(`/deposits/${payoutId}/link-existing`, {
        qbo_deposit_id: qboDepositId,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      qc.invalidateQueries({ queryKey: ['deposits', 'list'] });
      toast.success(t('deposits.duplicate_link_success'));
      setDuplicateDialog({ open: false, existing_deposits: [] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('deposits.duplicate_link_error'));
    },
  });

  const reassignMutation = useMutation({
    mutationFn: async ({ lineKey, accountName }) =>
      (await api.patch(`/deposits/${payoutId}/line-account`, {
        line_key: lineKey, account_name: accountName,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      toast.success(t('deposits.line_account_updated'));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('deposits.line_account_error'));
    },
  });

  const [splitDialog, setSplitDialog] = useState({ open: false, lineKey: null, totalAmount: 0 });

  const splitMutation = useMutation({
    mutationFn: async ({ lineKey, parts }) =>
      (await api.put(`/deposits/${payoutId}/line-splits`, { line_key: lineKey, parts })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      toast.success(t('deposits.line_split_saved'));
      setSplitDialog({ open: false, lineKey: null, totalAmount: 0 });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      if (d && typeof d === 'object' && d.reason === 'split_amount_mismatch') {
        toast.error(t('deposits.line_split_mismatch')
          .replace('{{parts}}', formatCurrency(d.parts_sum, locale))
          .replace('{{line}}', formatCurrency(d.line_amount, locale)), { duration: 10000 });
        return;
      }
      toast.error((typeof d === 'string' ? d : d?.message) || t('deposits.line_split_error'));
    },
  });

  const unsplitMutation = useMutation({
    mutationFn: async (lineKey) =>
      (await api.delete(`/deposits/${payoutId}/line-splits/${encodeURIComponent(lineKey)}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      toast.success(t('deposits.line_split_removed'));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('deposits.line_split_error'));
    },
  });

  if (!isAllowed) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground text-sm">
        {t('deposits.access_denied')}
      </div>
    );
  }

  if (q.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center"
           data-testid="deposito-not-reconciled">
        <XCircle className="h-10 w-10 text-muted-foreground/40" strokeWidth={1.5} />
        <div>
          <div className="text-sm font-semibold text-foreground">
            {t('deposits.not_reconciled_title')}
          </div>
          <div className="text-xs text-muted-foreground max-w-md mt-1">
            {t('deposits.not_reconciled_subtitle')}
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/depositos')}
          data-testid="deposito-not-reconciled-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>
      </div>
    );
  }

  const {
    preview, status, tiktok_verification: verification,
    payment_group: paymentGroup,
    reviewed, reviewed_at: reviewedAt, reviewed_by: reviewedBy,
    externally_reconciled_at, externally_reconciled_by,
    externally_reconciled_note, linked_qbo_deposit_id,
  } = q.data;
  const totals = preview?.totals || {};
  const recon = preview?.reconciliation || {};
  const payments = preview?.payments || [];
  const feeLines = preview?.fee_lines || [];
  const clawbackLines = preview?.clawback_lines || [];
  const reserveLine = preview?.reserve_line || null;
  const adjustmentLines = preview?.adjustment_lines || [];
  // Fix 31 (2026-09-09): agrupa por line_key — una línea dividida trae varias
  // entradas con el mismo line_key (una por parte).
  const adjustmentGroups = [];
  {
    const byKey = new Map();
    for (const line of adjustmentLines) {
      if (!byKey.has(line.line_key)) {
        byKey.set(line.line_key, []);
        adjustmentGroups.push({ lineKey: line.line_key, lines: byKey.get(line.line_key) });
      }
      byKey.get(line.line_key).push(line);
    }
  }
  const manualAdjustmentLines = preview?.manual_adjustment_lines || [];
  const shippingOnly = preview?.shipping_only_orders || [];
  const unmatched = preview?.unmatched_orders || [];
  const bankAccount = preview?.bank_account || {};

  const balanced = recon.balanced === true;
  const isCommitted = status === 'committed';
  const isExternallyReconciled = status === 'reconciled_externally';
  const isLocked = isCommitted || isExternallyReconciled;
  const hasSpecialCases = clawbackLines.length > 0 || reserveLine || adjustmentLines.length > 0 || shippingOnly.length > 0 || unmatched.length > 0;

  const FEE_LINE_KEYS = ['fee_referral', 'fee_shipping', 'fee_refund'];

  return (
    <div data-testid="deposito-detail-page">
      <PageHeader
        title={shortId(payoutId, 14, 4)}
        subtitle={
          <span className="flex items-center gap-2">
            {t('deposits.subtitle_detail')}
            {status === 'pending_review' && reviewed
              ? <ReviewedBadge testid="deposito-reviewed-badge" />
              : <StatusBadge status={status} />}
            {status === 'pending_review' && reviewed && reviewedBy && (
              <span className="text-[11px] text-muted-foreground"
                data-testid="deposito-reviewed-meta">
                {t('deposits.reviewed_meta')
                  .replace('{{by}}', reviewedBy)
                  .replace('{{at}}', formatDate(reviewedAt, locale))}
              </span>
            )}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={exporting}
                  data-testid="deposito-download-btn"
                >
                  {exporting
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <Download className="h-4 w-4 mr-2" />}
                  {t('deposits.btn_download')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="deposito-export-csv">
                  CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="deposito-export-xlsx">
                  Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/depositos')}
              data-testid="deposito-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
          </div>
        }
      />

      {/* ── Modo agregado: TikTok ya no expone las transacciones ────── */}
      {preview?.aggregate_mode && (
        <div
          className="flex items-start gap-3 rounded-xl px-5 py-3 mb-4 border"
          style={{ background: 'var(--warning-bg)', borderColor: 'var(--warning-border)', color: 'var(--warning-fg)' }}
          data-testid={preview?.adjustment_only
            ? 'deposito-adjustment-only-banner'
            : 'deposito-aggregate-mode-banner'}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">
              {t(preview?.adjustment_only
                ? 'deposits.adjustment_only_title'
                : 'deposits.aggregate_mode_title')}
            </p>
            <p className="text-xs mt-0.5">
              {t(preview?.adjustment_only
                ? 'deposits.adjustment_only_desc'
                : 'deposits.aggregate_mode_desc')}
            </p>
            {preview?.aggregate_revenue_line?.amount ? (
              <p className="text-xs mt-1" data-testid="deposito-aggregate-revenue-line">
                {preview.aggregate_revenue_line.account}:{' '}
                <span className="font-semibold">
                  {formatCurrency(preview.aggregate_revenue_line.amount, locale)}
                </span>
              </p>
            ) : null}
          </div>
        </div>
      )}

      {/* ── Pertenece a un pago bancario (grupo) ────────────────────── */}
      {paymentGroup?.payment_id && (
        <div
          className="flex items-center gap-3 rounded-xl px-5 py-3 mb-4 border"
          style={{ background: 'var(--info-bg)', borderColor: 'var(--info-border)', color: 'var(--info-fg)' }}
          data-testid="deposito-payment-group-banner"
        >
          <Building2 className="h-4 w-4 shrink-0" />
          <p className="flex-1 text-sm">
            {(paymentGroup.others_count > 0
              ? t('deposits.part_of_payout')
              : t('deposits.part_of_payout_single'))
              .replace('{{id}}', paymentGroup.payment_id)
              .replace('{{amount}}', formatCurrency(paymentGroup.total, locale))
              .replace('{{n}}', paymentGroup.others_count)}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/depositos?payment=${encodeURIComponent(paymentGroup.payment_id)}`)}
            data-testid="deposito-view-group-btn"
          >
            {t('deposits.view_group_btn')}
          </Button>
        </div>
      )}

      {/* ── Reconciliation banner ─────────────────────────────────── */}
      <div
        className="flex items-center gap-3 rounded-xl px-5 py-4 mb-6 border"
        style={balanced
          ? { background: 'var(--success-bg)', borderColor: 'var(--success-border)', color: 'var(--success-fg)' }
          : { background: 'var(--danger-bg)', borderColor: 'var(--danger-border)', color: 'var(--danger-fg)' }
        }
        data-testid={balanced ? 'deposito-banner-balanced' : 'deposito-banner-unbalanced'}
      >
        {balanced
          ? <CheckCircle2 className="h-5 w-5 shrink-0" />
          : <XCircle className="h-5 w-5 shrink-0" />}
        <div className="flex-1 text-sm font-semibold">
          {balanced
            ? t('deposits.banner_balanced')
            : `${t('deposits.banner_unbalanced')} ${formatCurrency(Math.abs(recon.diff), locale)} (diff)`}
        </div>
        {!isLocked && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => reviewMutation.mutate()}
              disabled={reviewMutation.isPending}
              data-testid="deposito-toggle-review-btn"
            >
              {reviewMutation.isPending
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <CheckCheck className="h-4 w-4 mr-2" />}
              {reviewed ? t('deposits.unmark_reviewed') : t('deposits.mark_reviewed')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMarkDialogOpen(true)}
              data-testid="deposito-mark-reconciled-btn"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              {t('deposits.btn_mark_reconciled')}
            </Button>
            <CommitButton
              balanced={balanced}
              net={totals.net}
              invoiceCount={payments.length}
              t={t}
              locale={locale}
              onConfirm={() => commitMutation.mutate()}
              isPending={commitMutation.isPending}
            />
          </div>
        )}
        {isCommitted && (
          <span className="text-sm font-medium opacity-75">{t('deposits.already_committed')}</span>
        )}
      </div>

      {/* ── Externally-reconciled banner (Fase 8) ───────────────────── */}
      {isExternallyReconciled && (
        <div
          className="flex items-center gap-3 rounded-xl px-5 py-4 mb-6 border"
          style={{ background: 'var(--info-bg)', borderColor: 'var(--info-border)', color: 'var(--info-fg)' }}
          data-testid="deposito-externally-reconciled-banner"
        >
          <ShieldCheck className="h-5 w-5 shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-semibold">{t('deposits.externally_reconciled_banner')}</p>
            <p className="text-xs opacity-90 mt-0.5" data-testid="deposito-externally-reconciled-meta">
              {t('deposits.externally_reconciled_by')} {externally_reconciled_by} · {formatDate(externally_reconciled_at, locale)}
              {externally_reconciled_note ? ` — "${externally_reconciled_note}"` : ''}
              {linked_qbo_deposit_id ? ` · ${t('deposits.externally_reconciled_linked')}: ${linked_qbo_deposit_id}` : ''}
            </p>
          </div>
          <UnmarkReconciledButton
            t={t}
            onConfirm={() => unmarkMutation.mutate()}
            isPending={unmarkMutation.isPending}
          />
        </div>
      )}

      {/* ── Why doesn't it balance? ─────────────────────────────────── */}
      {!balanced && !isLocked && (
        <WhyNotBalanced recon={recon} t={t} locale={locale} />
      )}

      {/* ── Warnings (unmatched only — truly blocking) ─────────────── */}
      {unmatched.length > 0 && (
        <div
          className="flex gap-3 rounded-xl border px-5 py-4 mb-6"
          style={{ background: 'var(--warning-bg)', borderColor: 'var(--warning-border)', color: 'var(--warning-fg)' }}
          data-testid="deposito-warnings"
        >
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">{t('deposits.warning_unmatched_blocking')}</p>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {unmatched.map((u) => (
                <span key={u.order_id} className="font-mono text-[10px] rounded px-1.5 py-0.5"
                  style={{ background: 'rgba(0,0,0,0.15)' }}
                  data-testid={`unmatched-order-${u.order_id}`}>
                  {u.order_id} ({formatCurrency(u.revenue_amount, locale)})
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Summary stats ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <MiniStat label={t('deposits.stat_gross')} value={formatCurrency(totals.payments_total, locale)} />
        {totals.clawbacks_total != null && totals.clawbacks_total !== 0 && (
          <MiniStat label={t('deposits.stat_clawbacks')} value={formatCurrency(totals.clawbacks_total, locale)} danger />
        )}
        <MiniStat label={t('deposits.stat_fees')} value={formatCurrency(totals.fees_total, locale)} danger />
        {totals.reserve_amount != null && totals.reserve_amount !== 0 && (
          <MiniStat label={t('deposits.stat_reserve')} value={formatCurrency(totals.reserve_amount, locale)} danger />
        )}
        {totals.adjustment_amount != null && totals.adjustment_amount !== 0 && (
          <MiniStat label={t('deposits.stat_adjustment')} value={formatCurrency(totals.adjustment_amount, locale)} danger />
        )}
        <MiniStat label={t('deposits.stat_net')} value={formatCurrency(totals.net, locale)} highlight />
        <MiniStat
          label={t('deposits.stat_bank')}
          value={bankAccount.name || '—'}
          extra={bankAccount.missing_account && (
            <span className="text-[10px]" style={{ color: 'var(--danger-fg)' }}>
              {t('deposits.account_missing')}
            </span>
          )}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── Payments table ──────────────────────────────────────── */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">
              {t('deposits.section_payments')}
              <span className="ml-2 text-muted-foreground font-normal text-xs">
                ({payments.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="deposito-payments-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Invoice</TableHead>
                  <TableHead className="text-xs">{t('common.customer')}</TableHead>
                  <TableHead className="text-xs">Orders</TableHead>
                  <TableHead className="text-xs text-right">{t('common.amount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-8">
                      {t('deposits.empty_payments')}
                    </TableCell>
                  </TableRow>
                )}
                {payments.map((p) => (
                  <TableRow key={p.invoice_id} data-testid={`payment-row-${p.invoice_id}`}>
                    <TableCell className="font-mono text-xs" title={`QBO Id ${p.invoice_id}`}>
                      {p.doc_number ? (
                        <>
                          <span data-testid={`payment-doc-number-${p.invoice_id}`}>
                            {p.doc_number}
                          </span>
                          <span className="block text-[10px] text-muted-foreground">
                            Id {p.invoice_id}
                          </span>
                        </>
                      ) : (
                        <span data-testid={`payment-doc-number-${p.invoice_id}`}>
                          {shortId(p.invoice_id, 8, 4)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {p.customer_name || shortId(p.qbo_customer, 8, 4)}
                      {p.manual_link && (
                        <span className="ml-1.5 rounded px-1 py-0.5 text-[9px] bg-amber-500/15 text-amber-600"
                          data-testid={`payment-manual-link-${p.invoice_id}`}>
                          manual
                        </span>
                      )}
                      {/* Fix 64 (2026-10-26): corregir la factura de una línea ya emparejada
                          (p. ej. la factura guardada se borró en QBO y se volvió a crear). */}
                      {!isLocked && (p.order_ids || []).length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-1.5 h-5 px-1.5 text-[10px]"
                          onClick={() => setLinkDialog({
                            open: true,
                            orderId: p.order_ids[0],
                            orderIds: p.order_ids,
                            docNumber: '',
                            amount: p.amount,
                            reason: 'change',
                            currentDoc: p.doc_number || p.invoice_id,
                          })}
                          data-testid={`change-invoice-btn-${p.invoice_id}`}
                        >
                          <Link2 className="h-3 w-3 mr-1" />
                          {t('deposits.change_invoice_btn')}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <span className="tabular-nums">{(p.order_ids || []).length}</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-medium">
                      {formatCurrency(p.amount, locale)}
                    </TableCell>
                  </TableRow>
                ))}
                {payments.length > 0 && (
                  <TableRow className="font-semibold border-t">
                    <TableCell colSpan={3} className="text-xs">{t('common.total')}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {formatCurrency(totals.payments_total, locale)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* ── Fee lines ───────────────────────────────────────────── */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">{t('deposits.section_fees')}</CardTitle>
            <p className="text-[11px] text-muted-foreground mt-1">{t('deposits.account_reassign_help')}</p>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="deposito-fees-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">{t('deposits.col_account')}</TableHead>
                  <TableHead className="text-xs text-right">{t('common.amount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feeLines.map((fl, i) => (
                  <TableRow key={i} data-testid={`fee-row-${i}`}>
                    <TableCell className="text-xs">
                      <div className="flex items-center gap-1.5">
                        <span>{fl.account}</span>
                        {/* Fix 62 (2026-10-20): reembolsos de Shipping Protection */}
                        {fl.line_key === 'shipping_protection' && (
                          <span className="text-[10px] text-muted-foreground" data-testid="fee-shipping-protection-label">
                            {t('deposits.shipping_protection_label')} ({(fl.related_orders || []).length})
                          </span>
                        )}
                        {fl.missing_account && (
                          <span className="text-[10px] font-medium rounded px-1 py-0.5"
                            style={{ background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}>
                            {t('deposits.account_missing')}
                          </span>
                        )}
                        {!isLocked && (
                          <AccountReassignControl
                            lineKey={fl.line_key || FEE_LINE_KEYS[i]}
                            currentAccount={fl.account}
                            accounts={eligibleAccounts}
                            onSave={(accountName) => reassignMutation.mutate({ lineKey: fl.line_key || FEE_LINE_KEYS[i], accountName })}
                            t={t}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-medium">
                      {formatCurrency(fl.amount, locale)}
                    </TableCell>
                  </TableRow>
                ))}
                {feeLines.length > 0 && (
                  <TableRow className="font-semibold border-t">
                    <TableCell className="text-xs">{t('common.total')}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {formatCurrency(totals.fees_total, locale)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Net summary */}
            <div
              className="flex items-center justify-between px-4 py-3 mt-2 rounded-b-[inherit] border-t"
              style={{ background: 'var(--surface-2)' }}
            >
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="h-4 w-4" />
                <span>{t('deposits.net_to_bank')}: <span className="font-mono text-xs">{bankAccount.name}</span></span>
              </div>
              <span className="text-base font-bold tabular-nums" data-testid="deposito-net-total">
                {formatCurrency(totals.net, locale)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Casos especiales ─────────────────────────────────────────── */}
      {hasSpecialCases && (
        <Card className="bg-card border-border mt-4" data-testid="deposito-special-cases">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" style={{ color: 'var(--warning-fg)' }} />
              {t('deposits.section_special_cases')}
              <span className="text-muted-foreground font-normal text-xs">
                {t('deposits.section_special_cases_hint')}
              </span>
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-1">{t('deposits.account_reassign_help')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Clawbacks */}
            {clawbackLines.length > 0 && (
              <SpecialCaseBlock
                label={`${t('deposits.case_clawbacks')} (${clawbackLines.length})`}
                testid="special-clawbacks"
                tone="danger"
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Order ID</TableHead>
                      <TableHead className="text-xs text-right">{t('common.amount')}</TableHead>
                      <TableHead className="text-xs">{t('deposits.col_account')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clawbackLines.map((cl) => (
                      <TableRow key={cl.order_id} data-testid={`clawback-row-${cl.order_id}`}>
                        <TableCell className="font-mono text-xs">{cl.order_id}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm font-medium" style={{ color: 'var(--danger-fg)' }}>
                          {formatCurrency(cl.revenue_amount, locale)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {cl.account}
                          {cl.missing_account && (
                            <span className="ml-1.5 text-[10px] font-medium rounded px-1 py-0.5"
                              style={{ background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}>
                              {t('deposits.account_missing')}
                            </span>
                          )}
                          {!isLocked && (
                            <AccountReassignControl
                              lineKey="clawbacks"
                              currentAccount={cl.account}
                              accounts={eligibleAccounts}
                              onSave={(accountName) => reassignMutation.mutate({ lineKey: 'clawbacks', accountName })}
                              t={t}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SpecialCaseBlock>
            )}

            {/* Reserve */}
            {reserveLine && (
              <SpecialCaseBlock
                label={t('deposits.case_reserve')}
                testid="special-reserve"
                tone="warning"
              >
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">{reserveLine.account}</span>
                  {reserveLine.missing_account && (
                    <span className="text-[10px] font-medium rounded px-1 py-0.5"
                      style={{ background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}>
                      {t('deposits.account_missing')}
                    </span>
                  )}
                  {!isLocked && (
                    <AccountReassignControl
                      lineKey="reserve"
                      currentAccount={reserveLine.account}
                      accounts={eligibleAccounts}
                      onSave={(accountName) => reassignMutation.mutate({ lineKey: 'reserve', accountName })}
                      t={t}
                    />
                  )}
                  <span className="ml-auto tabular-nums font-semibold" data-testid="reserve-amount">
                    {formatCurrency(reserveLine.amount, locale)}
                  </span>
                </div>
              </SpecialCaseBlock>
            )}

            {/* Adjustments — one line per reason (Fase 6), splittable across
                several accounts (Fix 31, 2026-09-09) */}
            {adjustmentGroups.length > 0 && (
              <SpecialCaseBlock
                label={t('deposits.case_adjustment')}
                testid="special-adjustment"
                tone="warning"
              >
                <div className="space-y-3">
                  {adjustmentGroups.map(({ lineKey, lines }) => {
                    const isSplit = lines.length > 1 || lines[0]?.is_split;
                    const totalAmount = lines.reduce((acc, l) => acc + (l.amount || 0), 0);
                    return (
                      <div key={lineKey} className="space-y-1.5" data-testid={`adjustment-line-${lineKey}`}>
                        <div className="flex items-center gap-3 text-sm flex-wrap">
                          <span className="font-medium" data-testid={`adjustment-reason-${lineKey}`}>
                            {lines[0].reason_label}
                          </span>
                          {lines[0].uncategorized && (
                            <span className="text-[10px] font-medium rounded px-1 py-0.5"
                              data-testid={`adjustment-uncategorized-${lineKey}`}
                              style={{ background: 'var(--warning-bg)', color: 'var(--warning-fg)' }}>
                              {t('deposits.adjustment_uncategorized')}
                            </span>
                          )}
                          {lines[0].split_invalid && (
                            <span className="text-[10px] font-medium rounded px-1 py-0.5"
                              data-testid={`adjustment-split-invalid-${lineKey}`}
                              style={{ background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}>
                              {t('deposits.line_split_invalid')}
                            </span>
                          )}
                          {!isLocked && !isSplit && (
                            <Button
                              variant="outline" size="sm" className="h-6 px-2 text-[10px]"
                              onClick={() => setSplitDialog({ open: true, lineKey, totalAmount: lines[0].amount })}
                              data-testid={`line-split-open-btn-${lineKey}`}
                            >
                              {t('deposits.line_split_btn')}
                            </Button>
                          )}
                          {!isLocked && isSplit && (
                            <Button
                              variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                              onClick={() => unsplitMutation.mutate(lineKey)}
                              disabled={unsplitMutation.isPending}
                              data-testid={`line-split-remove-btn-${lineKey}`}
                            >
                              <Undo2 className="h-3 w-3 mr-1" />
                              {t('deposits.line_split_remove_btn')}
                            </Button>
                          )}
                          <span className="ml-auto tabular-nums font-semibold"
                            data-testid={`adjustment-amount-${lineKey}`}>
                            {formatCurrency(totalAmount, locale)}
                          </span>
                        </div>
                        {lines.map((line, idx) => (
                          <div key={line.split_id || idx}
                            className={`flex items-center gap-3 text-sm flex-wrap ${isSplit ? 'pl-4' : ''}`}
                            data-testid={`adjustment-part-${lineKey}-${idx}`}>
                            {isSplit && (
                              <span className="text-[10px] text-muted-foreground shrink-0"
                                data-testid={`adjustment-part-index-${lineKey}-${idx}`}>
                                {t('deposits.line_split_part_label')
                                  .replace('{{n}}', String(idx + 1))
                                  .replace('{{total}}', String(lines.length))}
                              </span>
                            )}
                            <span className="text-muted-foreground">{line.account}</span>
                            {line.missing_account && (
                              <span className="text-[10px] font-medium rounded px-1 py-0.5"
                                data-testid={`adjustment-part-missing-${lineKey}-${idx}`}
                                style={{ background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}>
                                {t('deposits.account_missing')}
                              </span>
                            )}
                            {!isLocked && !isSplit && (
                              <AccountReassignControl
                                lineKey={lineKey}
                                currentAccount={line.account}
                                accounts={eligibleAccounts}
                                onSave={(accountName) => reassignMutation.mutate({ lineKey, accountName })}
                                t={t}
                              />
                            )}
                            {isSplit && (
                              <span className="ml-auto tabular-nums text-xs font-medium"
                                data-testid={`adjustment-part-amount-${lineKey}-${idx}`}>
                                {formatCurrency(line.amount, locale)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </SpecialCaseBlock>
            )}

            {/* Shipping-only orders */}
            {shippingOnly.length > 0 && (
              <SpecialCaseBlock
                label={`${t('deposits.case_shipping_only')} (${shippingOnly.length})`}
                testid="special-shipping-only"
                tone="neutral"
              >
                <div className="flex flex-wrap gap-1">
                  {shippingOnly.map((s) => (
                    <span key={s.order_id} className="font-mono text-[10px] rounded px-1.5 py-0.5 bg-muted"
                      data-testid={`shipping-only-${s.order_id}`}>
                      {s.order_id}
                    </span>
                  ))}
                </div>
              </SpecialCaseBlock>
            )}

            {/* Unmatched orders */}
            {unmatched.length > 0 && (
              <SpecialCaseBlock
                label={`${t('deposits.case_unmatched')} (${unmatched.length})`}
                testid="special-unmatched"
                tone="warning"
              >
                <div className="space-y-1.5">
                  {unmatched.map((u) => (
                    <div key={u.order_id} className="flex items-center gap-2 flex-wrap"
                      data-testid={`unmatched-row-${u.order_id}`}>
                      <span className="font-mono text-[10px] rounded px-1.5 py-0.5"
                        style={{ background: 'rgba(0,0,0,0.15)' }}>
                        {u.order_id} ({formatCurrency(u.revenue_amount, locale)})
                      </span>
                      {u.reason && (
                        <span className="text-[10px] opacity-80"
                          data-testid={`unmatched-reason-${u.order_id}`}>
                          {u.reason === 'no_estimate'
                            ? (lang === 'es' ? 'sin estimate en el sistema' : 'no estimate in system')
                            : (lang === 'es' ? 'estimate sin factura vinculada' : 'estimate without linked invoice')}
                        </span>
                      )}
                      {!isLocked && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[10px]"
                          onClick={() => setLinkDialog({
                            open: true,
                            orderId: u.order_id,
                            docNumber: '',
                            amount: u.revenue_amount,
                            reason: u.reason,
                          })}
                          data-testid={`link-invoice-btn-${u.order_id}`}
                        >
                          <Link2 className="h-3 w-3 mr-1" />
                          {t('deposits.link_invoice_btn')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </SpecialCaseBlock>
            )}
          </CardContent>
        </Card>
      )}

      <ManualAdjustmentsSection
        payoutId={payoutId}
        adjustments={manualAdjustmentLines}
        accounts={eligibleAccounts}
        suggestedAmount={-(recon.diff || 0)}
        isCommitted={isLocked}
        isExternallyReconciled={isExternallyReconciled}
        t={t}
        locale={locale}
        qc={qc}
      />

      <TikTokVerifyPanel payoutId={payoutId} verification={verification} t={t} locale={locale} />

      <Dialog open={linkDialog.open}
        onOpenChange={(o) => !o && !linkInvoiceMutation.isPending && closeLinkDialog()}>
        <DialogContent data-testid="link-invoice-dialog">
          <DialogHeader>
            <DialogTitle>{t('deposits.link_invoice_title')}</DialogTitle>
            <DialogDescription>{t('deposits.link_invoice_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="font-mono text-xs" data-testid="link-invoice-order-id">
              {linkDialog.orderId}
            </p>
            <div className="rounded-md p-2.5 space-y-1.5"
              style={{ background: 'var(--border-subtle)' }}>
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t('deposits.link_invoice_order_amount')}
                </span>
                <span className="text-sm font-semibold tabular-nums text-foreground"
                  data-testid="link-invoice-order-amount">
                  {formatCurrency(linkDialog.amount, locale)}
                </span>
              </div>
              {linkDialog.reason && (
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t('deposits.link_invoice_reason')}
                  </span>
                  <span className="text-xs text-foreground/90"
                    data-testid="link-invoice-order-reason">
                    {linkDialog.reason === 'change'
                      ? t('deposits.link_invoice_reason_change')
                        .replace('{{doc}}', linkDialog.currentDoc || '')
                        .replace('{{n}}', String((linkDialog.orderIds || []).length || 1))
                      : linkDialog.reason === 'no_estimate'
                        ? t('deposits.link_invoice_reason_no_estimate')
                        : t('deposits.link_invoice_reason_no_invoice')}
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="link-invoice-doc" className="text-xs">DocNumber</Label>
              <Input
                id="link-invoice-doc"
                value={linkDialog.docNumber}
                placeholder={t('deposits.link_invoice_placeholder')}
                onChange={(e) => setLinkDialog((p) => ({ ...p, docNumber: e.target.value }))}
                className="h-8 w-40 text-xs"
                data-testid="link-invoice-doc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm"
              onClick={closeLinkDialog}
              disabled={linkInvoiceMutation.isPending}
              data-testid="link-invoice-cancel-btn">
              {t('common.cancel')}
            </Button>
            <Button size="sm"
              onClick={() => linkInvoiceMutation.mutate({
                orderId: linkDialog.orderId,
                orderIds: linkDialog.orderIds,  // Fix 64
                docNumber: linkDialog.docNumber.trim() })}
              disabled={linkInvoiceMutation.isPending || !linkDialog.docNumber.trim()}
              data-testid="link-invoice-confirm-btn">
              {linkInvoiceMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('deposits.link_invoice_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MarkReconciledDialog
        open={markDialogOpen}
        onOpenChange={setMarkDialogOpen}
        onConfirm={(vals) => markMutation.mutate(vals)}
        isPending={markMutation.isPending}
        t={t}
      />

      <DuplicateDepositDialog
        open={duplicateDialog.open}
        onOpenChange={(open) => setDuplicateDialog((prev) => ({ ...prev, open }))}
        existingDeposits={duplicateDialog.existing_deposits}
        onLink={(qboDepositId) => linkExistingMutation.mutate(qboDepositId)}
        onForceCreate={() => commitMutation.mutate({ force: true })}
        isLinking={linkExistingMutation.isPending}
        isForcing={commitMutation.isPending}
        t={t}
        locale={locale}
      />

      <LineSplitDialog
        open={splitDialog.open}
        onOpenChange={(o) => setSplitDialog((prev) => ({ ...prev, open: o }))}
        lineKey={splitDialog.lineKey}
        totalAmount={splitDialog.totalAmount}
        accounts={eligibleAccounts}
        onSave={(parts) => splitMutation.mutate({ lineKey: splitDialog.lineKey, parts })}
        isPending={splitMutation.isPending}
        t={t}
        locale={locale}
      />
    </div>
  );
}

/* ── Sub-components ────────────────────────────────────────────────────── */

function SpecialCaseBlock({ label, testid, tone, children }) {
  const colors = {
    danger: { bg: 'var(--danger-bg)', border: 'var(--danger-border)', fg: 'var(--danger-fg)' },
    warning: { bg: 'var(--warning-bg)', border: 'var(--warning-border)', fg: 'var(--warning-fg)' },
    neutral: { bg: 'hsl(var(--muted))', border: 'hsl(var(--border))', fg: 'hsl(var(--muted-foreground))' },
  }[tone] || {};
  return (
    <div
      className="rounded-lg border p-3"
      style={{ background: colors.bg, borderColor: colors.border }}
      data-testid={testid}
    >
      <p className="text-xs font-semibold mb-2" style={{ color: colors.fg }}>{label}</p>
      {children}
    </div>
  );
}

/* ── ¿Por qué no cuadra? ──────────────────────────────────────────────── */
function WhyNotBalanced({ recon, t, locale }) {
  const bd = recon?.diff_breakdown || {};
  const hasUnmatched = (bd.unmatched_orders_count || 0) > 0;
  const hasManualAdj = (bd.manual_adjustments_total || 0) !== 0;
  return (
    <div
      className="rounded-xl border px-5 py-4 mb-6 text-sm"
      style={{ background: 'var(--warning-bg)', borderColor: 'var(--warning-border)', color: 'var(--warning-fg)' }}
      data-testid="deposito-why-not-balanced"
    >
      <div className="flex items-center gap-2 font-semibold mb-1.5">
        <HelpCircle className="h-4 w-4 shrink-0" />
        {t('deposits.why_not_balanced')}
      </div>
      <div className="text-xs space-y-1 opacity-90">
        {hasUnmatched && (
          <p data-testid="why-unmatched-breakdown">
            {t('deposits.why_missing_amount')} {formatCurrency(bd.unmatched_orders_total, locale)} — {bd.unmatched_orders_count}{' '}
            {t('deposits.why_unmatched_orders')} {formatCurrency(bd.unmatched_orders_total, locale)}.
          </p>
        )}
        {hasManualAdj && (
          <p data-testid="why-manual-adj-applied">
            {t('deposits.why_manual_adj_applied')}: <span className="font-semibold">{formatCurrency(bd.manual_adjustments_total, locale)}</span>
          </p>
        )}
        {/* SINGLE source of truth — same number as the banner above and the
            manual-adjustment default (recon.diff), never a second contradicting figure. */}
        <p data-testid="why-diff-total">
          {t('deposits.why_diff_total')}: <span className="font-semibold">{formatCurrency(Math.abs(recon.diff || 0), locale)}</span>
        </p>
      </div>
    </div>
  );
}

/* ── Inline account reassignment control (fee/clawback/reserve/adjustment) */
function AccountReassignControl({ lineKey, currentAccount, accounts, onSave, t }) {
  const [open, setOpen] = useState(false);
  return (
    <Select
      open={open}
      onOpenChange={setOpen}
      value={currentAccount || ''}
      onValueChange={(val) => { onSave(val); setOpen(false); }}
    >
      <SelectTrigger
        className="h-6 px-2 gap-1 text-[11px] font-medium rounded-md border shadow-none [&>svg]:hidden hover:bg-accent transition-colors"
        style={{ borderColor: 'hsl(var(--border))' }}
        data-testid={`reassign-account-${lineKey}`}
        title={t('deposits.account_reassign_help')}
      >
        <Pencil className="h-3 w-3" />
        <span>{t('deposits.line_account_change')}</span>
      </SelectTrigger>
      <SelectContent>
        {accounts.map((a) => (
          <SelectItem key={a.name} value={a.name} data-testid={`reassign-option-${lineKey}-${a.name}`}>
            {a.name}{a.missing ? ` (${t('deposits.account_eligible_missing')})` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* ── Split de una línea de ajuste entre varias cuentas (Fix 31, 2026-09-09) */
function LineSplitDialog({ open, onOpenChange, lineKey, totalAmount, accounts, onSave, isPending, t, locale }) {
  const emptyPart = () => ({ key: Math.random().toString(36).slice(2), account_name: '', amount: '', note: '' });
  const [parts, setParts] = useState([emptyPart(), emptyPart()]);

  useEffect(() => {
    if (open) setParts([emptyPart(), emptyPart()]);
  }, [open, lineKey]);

  const sum = parts.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
  const remaining = Math.round(((totalAmount || 0) - sum) * 100) / 100;
  const allFilled = parts.every((p) => p.account_name && p.amount !== '' && parseFloat(p.amount) !== 0);
  const canSave = allFilled && remaining === 0 && parts.length >= 2 && !isPending;

  const updatePart = (idx, field, value) => setParts((prev) =>
    prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  const addPart = () => setParts((prev) => [...prev, emptyPart()]);
  const removePart = (idx) => setParts((prev) => prev.filter((_, i) => i !== idx));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isPending && onOpenChange(false)}>
      <DialogContent data-testid="line-split-dialog">
        <DialogHeader>
          <DialogTitle>{t('deposits.line_split_title')}</DialogTitle>
          <DialogDescription>
            {t('deposits.line_split_desc')} — <span className="font-semibold" data-testid="line-split-total-amount">
              {formatCurrency(totalAmount, locale)}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {parts.map((p, idx) => (
            <div key={p.key} className="flex items-start gap-2" data-testid={`line-split-part-row-${idx}`}>
              <div className="flex-1 space-y-1.5">
                <Select value={p.account_name} onValueChange={(v) => updatePart(idx, 'account_name', v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`line-split-account-${idx}`}>
                    <SelectValue placeholder={t('deposits.line_split_pick_account')} />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.name} value={a.name} data-testid={`line-split-account-opt-${idx}-${a.name}`}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number" step="0.01"
                  value={p.amount}
                  onChange={(e) => updatePart(idx, 'amount', e.target.value)}
                  placeholder="0.00"
                  className="h-8 text-xs"
                  data-testid={`line-split-amount-${idx}`}
                />
                <Input
                  value={p.note}
                  onChange={(e) => updatePart(idx, 'note', e.target.value)}
                  placeholder={t('deposits.line_split_note_placeholder')}
                  className="h-8 text-xs"
                  data-testid={`line-split-note-${idx}`}
                />
              </div>
              {parts.length > 2 && (
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                  onClick={() => removePart(idx)} data-testid={`line-split-remove-row-btn-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addPart} data-testid="line-split-add-part-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> {t('deposits.line_split_add_part')}
          </Button>
          <div className="text-xs flex items-center justify-between rounded-md p-2"
            style={{
              background: remaining === 0 ? 'var(--success-bg)' : 'var(--warning-bg)',
              color: remaining === 0 ? 'var(--success-fg)' : 'var(--warning-fg)',
            }}
            data-testid="line-split-remaining">
            <span>{t('deposits.line_split_remaining')}</span>
            <span className="font-semibold tabular-nums">{formatCurrency(remaining, locale)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
            disabled={isPending} data-testid="line-split-cancel-btn">
            {t('common.cancel')}
          </Button>
          <Button size="sm" disabled={!canSave}
            onClick={() => onSave(parts.map((p) => ({
              account_name: p.account_name,
              amount: parseFloat(p.amount),
              note: p.note || undefined,
            })))}
            data-testid="line-split-save-btn">
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('deposits.line_split_save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Manual adjustments section ───────────────────────────────────────── */
function ManualAdjustmentsSection({ payoutId, adjustments, accounts, suggestedAmount, isCommitted, isExternallyReconciled, t, locale, qc }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [accountName, setAccountName] = useState('');
  const [note, setNote] = useState('');

  const openDialog = () => {
    setAmount(suggestedAmount ? suggestedAmount.toFixed(2) : '');
    setAccountName('');
    setNote('');
    setDialogOpen(true);
  };

  const addMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/deposits/${payoutId}/manual-adjustments`, {
        amount: parseFloat(amount), account_name: accountName, note: note || undefined,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      toast.success(t('deposits.manual_adj_added'));
      setDialogOpen(false);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('deposits.manual_adj_error'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (adjId) =>
      (await api.delete(`/deposits/${payoutId}/manual-adjustments/${adjId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      toast.success(t('deposits.manual_adj_deleted'));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('deposits.manual_adj_delete_error'));
    },
  });

  const canSave = amount && parseFloat(amount) !== 0 && accountName;

  return (
    <Card className="bg-card border-border mt-4" data-testid="manual-adjustments-section">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold">
          {t('deposits.manual_adj_section')}
          <span className="ml-2 text-muted-foreground font-normal text-xs">({adjustments.length})</span>
        </CardTitle>
        {!isCommitted && (
          <Button size="sm" variant="outline" onClick={openDialog} data-testid="manual-adj-add-btn">
            <Plus className="h-4 w-4 mr-1.5" />
            {t('deposits.manual_adj_add_btn')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isCommitted && (
          <p className="text-xs text-muted-foreground mb-3">
            {isExternallyReconciled
              ? t('deposits.manual_adj_readonly_hint_external')
              : t('deposits.manual_adj_readonly_hint')}
          </p>
        )}
        {adjustments.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="manual-adj-empty">{t('deposits.manual_adj_empty')}</p>
        ) : (
          <Table data-testid="manual-adjustments-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">{t('deposits.col_account')}</TableHead>
                <TableHead className="text-xs">{t('deposits.manual_adj_note')}</TableHead>
                <TableHead className="text-xs">{t('common.date')}</TableHead>
                <TableHead className="text-xs text-right">{t('common.amount')}</TableHead>
                {!isCommitted && <TableHead className="text-xs w-8" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {adjustments.map((adj) => (
                <TableRow key={adj.id} data-testid={`manual-adj-row-${adj.id}`}>
                  <TableCell className="text-xs">
                    {adj.account}
                    {adj.missing_account && (
                      <span className="ml-1.5 text-[10px] font-medium rounded px-1 py-0.5"
                        style={{ background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}>
                        {t('deposits.account_missing')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {adj.note || '—'}
                    <div className="text-[10px] opacity-75">
                      {t('deposits.manual_adj_by')} {adj.created_by} · {formatDate(adj.created_at, locale)}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(adj.created_at, locale)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm font-medium">
                    {formatCurrency(adj.amount, locale)}
                  </TableCell>
                  {!isCommitted && (
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => {
                          if (deleteMutation.isPending) return;
                          deleteMutation.mutate(adj.id);
                        }}
                        disabled={deleteMutation.isPending && deleteMutation.variables === adj.id}
                        data-testid={`manual-adj-delete-${adj.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="manual-adj-dialog">
          <DialogHeader>
            <DialogTitle>{t('deposits.manual_adj_dialog_title')}</DialogTitle>
            <DialogDescription>{t('deposits.manual_adj_dialog_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="manual-adj-amount">{t('deposits.manual_adj_amount')}</Label>
              <Input
                id="manual-adj-amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="manual-adj-amount-input"
              />
              <p className="text-[11px] text-muted-foreground">{t('deposits.manual_adj_amount_hint')}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t('deposits.manual_adj_account')}</Label>
              <Select value={accountName} onValueChange={setAccountName}>
                <SelectTrigger data-testid="manual-adj-account-select">
                  <SelectValue placeholder={t('deposits.manual_adj_account_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.name} value={a.name} data-testid={`manual-adj-account-option-${a.name}`}>
                      {a.name}{a.missing ? ` (${t('deposits.account_eligible_missing')})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-adj-note">{t('deposits.manual_adj_note')}</Label>
              <Textarea
                id="manual-adj-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="manual-adj-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)} data-testid="manual-adj-cancel-btn">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (!canSave || addMutation.isPending) return;
                addMutation.mutate();
              }}
              disabled={!canSave || addMutation.isPending}
              data-testid="manual-adj-save-btn"
            >
              {addMutation.isPending
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('deposits.manual_adj_saving')}</>
                : t('deposits.manual_adj_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MiniStat({ label, value, extra, highlight, danger }) {  return (
    <Card className="bg-card border-border">
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div
          className="mt-1 text-base font-semibold tabular-nums leading-snug"
          style={{
            color: highlight ? 'var(--success-fg)' : danger ? 'var(--danger-fg)' : undefined,
          }}
        >
          {value}
        </div>
        {extra && <div className="mt-1">{extra}</div>}
      </CardContent>
    </Card>
  );
}

function CommitButton({ balanced, net, invoiceCount, t, locale, onConfirm, isPending }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant={balanced ? 'default' : 'outline'}
          disabled={!balanced || isPending}
          className="shrink-0"
          data-testid="deposito-commit-btn"
          title={!balanced ? t('deposits.commit_disabled_hint') : undefined}
        >
          {isPending
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : t('deposits.btn_commit')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="deposito-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('deposits.confirm_title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('deposits.confirm_body')
              .replace('{{net}}', formatCurrency(net, locale))
              .replace('{{count}}', invoiceCount)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="deposito-confirm-cancel">
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="deposito-confirm-ok"
          >
            {t('common.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ── Reconciliado fuera del sistema (Fase 8) ─────────────────────────── */
function UnmarkReconciledButton({ t, onConfirm, isPending }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" className="shrink-0" data-testid="deposito-unmark-btn">
          {isPending
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <Undo2 className="h-3.5 w-3.5 mr-1.5" />}
          {t('deposits.unmark_reconciled_btn')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="deposito-unmark-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('deposits.unmark_reconciled_confirm_title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('deposits.unmark_reconciled_confirm_desc')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="deposito-unmark-cancel">{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="deposito-unmark-confirm-ok">
            {t('common.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MarkReconciledDialog({ open, onOpenChange, onConfirm, isPending, t }) {
  const [note, setNote] = useState('');
  const [qboId, setQboId] = useState('');

  useEffect(() => {
    if (open) { setNote(''); setQboId(''); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mark-reconciled-dialog">
        <DialogHeader>
          <DialogTitle>{t('deposits.mark_reconciled_dialog_title')}</DialogTitle>
          <DialogDescription>{t('deposits.mark_reconciled_dialog_desc')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="mark-reconciled-note">{t('deposits.mark_reconciled_note')}</Label>
            <Textarea
              id="mark-reconciled-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="mark-reconciled-note-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mark-reconciled-qbo-id">{t('deposits.mark_reconciled_qbo_id')}</Label>
            <Input
              id="mark-reconciled-qbo-id"
              value={qboId}
              onChange={(e) => setQboId(e.target.value)}
              placeholder={t('deposits.mark_reconciled_qbo_id_placeholder')}
              data-testid="mark-reconciled-qbo-id-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="mark-reconciled-cancel-btn">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => { if (!isPending) onConfirm({ note, qboDepositId: qboId }); }}
            disabled={isPending}
            data-testid="mark-reconciled-confirm-btn"
          >
            {isPending
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('deposits.mark_reconciled_confirm')}</>
              : t('deposits.mark_reconciled_confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DuplicateDepositDialog({ open, onOpenChange, existingDeposits, onLink, onForceCreate, isLinking, isForcing, t, locale }) {
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (open) setSelectedId(existingDeposits?.[0]?.id || null);
  }, [open, existingDeposits]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="duplicate-deposit-dialog">
        <DialogHeader>
          <DialogTitle>{t('deposits.duplicate_dialog_title')}</DialogTitle>
          <DialogDescription>{t('deposits.duplicate_dialog_desc')}</DialogDescription>
        </DialogHeader>
        <Table data-testid="duplicate-deposits-table">
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs w-8" />
              <TableHead className="text-xs">{t('deposits.duplicate_col_id')}</TableHead>
              <TableHead className="text-xs">{t('deposits.duplicate_col_date')}</TableHead>
              <TableHead className="text-xs text-right">{t('deposits.duplicate_col_amount')}</TableHead>
              <TableHead className="text-xs">{t('deposits.duplicate_col_note')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(existingDeposits || []).map((d) => (
              <TableRow
                key={d.id}
                className="cursor-pointer"
                onClick={() => setSelectedId(d.id)}
                data-testid={`duplicate-row-${d.id}`}
              >
                <TableCell>
                  <input
                    type="radio"
                    checked={selectedId === d.id}
                    onChange={() => setSelectedId(d.id)}
                    data-testid={`duplicate-radio-${d.id}`}
                  />
                </TableCell>
                <TableCell className="font-mono text-xs">{d.id}</TableCell>
                <TableCell className="text-xs">{d.txn_date}</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{formatCurrency(d.total_amt, locale)}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{d.private_note}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="duplicate-cancel-btn">
            {t('common.cancel')}
          </Button>
          <Button variant="outline" onClick={onForceCreate} disabled={isForcing} data-testid="duplicate-force-btn">
            {isForcing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {t('deposits.duplicate_force_btn')}
          </Button>
          <Button onClick={() => onLink(selectedId)} disabled={!selectedId || isLinking} data-testid="duplicate-link-btn">
            {isLinking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
            {t('deposits.duplicate_link_btn')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TikTokVerifyPanel({ payoutId, verification, t, locale }) {
  const qc = useQueryClient();
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const verifyMutation = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return (await api.post(`/deposits/${payoutId}/verify-tiktok-excel`, fd)).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['deposits', 'detail', payoutId] });
      if (data.verdict === 'match') {
        toast.success(t('deposits.verify_toast_match'));
      } else {
        toast.warning(t('deposits.verify_toast_diff'));
      }
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : t('deposits.verify_error');
      toast.error(msg);
    },
  });

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error(t('deposits.verify_invalid_ext'));
      return;
    }
    verifyMutation.mutate(f);
  };

  const fresh = verifyMutation.data;
  const result = fresh || verification?.summary;
  const meta = fresh ? { at: fresh.at, by: fresh.by } : verification;
  const isMatch = result?.verdict === 'match';

  return (
    <Card className="bg-card border-border mt-4" data-testid="tiktok-verify-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4" />
          {t('deposits.verify_section_title')}
          <span className="text-muted-foreground font-normal text-xs">
            {t('deposits.verify_section_hint')}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="tiktok-verify-file-input"
        />
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileInputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-8 cursor-pointer transition-colors"
          style={{
            borderColor: dragOver ? 'hsl(var(--primary))' : 'hsl(var(--border))',
            background: dragOver ? 'var(--surface-2)' : 'transparent',
          }}
          data-testid="tiktok-verify-dropzone"
        >
          {verifyMutation.isPending
            ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            : <UploadCloud className="h-6 w-6 text-muted-foreground" />}
          <p className="text-sm font-medium">
            {verifyMutation.isPending ? t('deposits.verify_uploading') : t('deposits.verify_dropzone_cta')}
          </p>
          {!verifyMutation.isPending && (
            <p className="text-xs text-muted-foreground">{t('deposits.verify_dropzone_sub')}</p>
          )}
        </div>

        {result && (
          <div className="space-y-3" data-testid="tiktok-verify-result">
            {isMatch ? (
              <div
                className="flex items-center gap-3 rounded-lg border px-4 py-3"
                style={{ background: 'var(--success-bg)', borderColor: 'var(--success-border)', color: 'var(--success-fg)' }}
                data-testid="tiktok-verify-banner-match"
              >
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                <span className="text-sm font-semibold">{t('deposits.verify_match_banner')}</span>
              </div>
            ) : (
              <div
                className="flex items-center gap-3 rounded-lg border px-4 py-3"
                style={{ background: 'var(--warning-bg)', borderColor: 'var(--warning-border)', color: 'var(--warning-fg)' }}
                data-testid="tiktok-verify-banner-diff"
              >
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <span className="text-sm font-semibold">{t('deposits.verify_diff_banner')}</span>
              </div>
            )}

            {meta?.at && (
              <p className="text-xs text-muted-foreground" data-testid="tiktok-verify-last-meta">
                {t('deposits.verify_last_at')} {formatDate(meta.at, locale)} · {meta.by}
              </p>
            )}

            <Table data-testid="tiktok-verify-totals-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">{t('deposits.verify_col_field')}</TableHead>
                  <TableHead className="text-xs text-right">TikTok</TableHead>
                  <TableHead className="text-xs text-right">{t('deposits.verify_col_platform')}</TableHead>
                  <TableHead className="text-xs text-right">{t('deposits.verify_col_diff')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(result.totals || []).map((row, i) => (
                  <TableRow
                    key={i}
                    data-testid={`tiktok-verify-total-row-${i}`}
                    style={!row.match ? { background: 'var(--danger-bg)' } : undefined}
                  >
                    <TableCell className="text-xs">{row.campo}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{formatCurrency(row.tiktok, locale)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{formatCurrency(row.plataforma, locale)}</TableCell>
                    <TableCell
                      className="text-right text-xs tabular-nums font-medium"
                      style={{ color: row.match ? undefined : 'var(--danger-fg)' }}
                    >
                      {formatCurrency(row.diff, locale)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {result.orders?.order_details_available && (
              <div className="text-xs space-y-3">
                <p className="text-muted-foreground" data-testid="tiktok-verify-orders-matched">
                  {t('deposits.verify_orders_matched')}: <span className="font-semibold">{result.orders.matched_count}</span>
                  {typeof result.orders.excel_rows_total === 'number' && (
                    <span className="ml-2 opacity-70" data-testid="tiktok-verify-excel-rows-read">
                      · {t('deposits.verify_excel_rows_read')}: {result.orders.excel_rows_total}
                      {' '}({result.orders.excel_rows_matched_statement} {t('deposits.verify_rows_this_statement')},{' '}
                      {result.orders.excel_orders_count} {t('deposits.verify_orders_in_excel')})
                    </span>
                  )}
                </p>
                {result.orders.missing_in_platform?.length > 0 && (
                  <OrderDiffList
                    title={t('deposits.verify_missing_in_platform')}
                    items={result.orders.missing_in_platform}
                    valueKey="tiktok"
                    testid="missing-in-platform"
                    locale={locale}
                  />
                )}
                {result.orders.missing_in_tiktok?.length > 0 && (
                  <OrderDiffList
                    title={t('deposits.verify_missing_in_tiktok')}
                    items={result.orders.missing_in_tiktok}
                    valueKey="plataforma"
                    testid="missing-in-tiktok"
                    locale={locale}
                  />
                )}
                {result.orders.amount_mismatches?.length > 0 && (
                  <div data-testid="tiktok-verify-amount-mismatches">
                    <p className="font-semibold mb-1" style={{ color: 'var(--danger-fg)' }}>
                      {t('deposits.verify_amount_mismatches')} ({result.orders.amount_mismatches.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {result.orders.amount_mismatches.map((o) => (
                        <span
                          key={o.order_id}
                          className="font-mono text-[10px] rounded px-1.5 py-0.5"
                          style={{ background: 'var(--danger-bg)', color: 'var(--danger-fg)' }}
                          data-testid={`amount-mismatch-${o.order_id}`}
                        >
                          {o.order_id} ({o.campo}): {formatCurrency(o.tiktok, locale)} vs {formatCurrency(o.plataforma, locale)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              data-testid="tiktok-verify-reverify-btn"
            >
              {t('deposits.verify_reverify')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OrderDiffList({ title, items, valueKey, testid, locale }) {
  return (
    <div data-testid={`tiktok-verify-${testid}`}>
      <p className="font-semibold mb-1" style={{ color: 'var(--warning-fg)' }}>{title} ({items.length})</p>
      <div className="flex flex-wrap gap-1">
        {items.map((o) => (
          <span key={o.order_id} className="font-mono text-[10px] rounded px-1.5 py-0.5 bg-muted">
            {o.order_id} ({formatCurrency(o[valueKey], locale)})
          </span>
        ))}
      </div>
    </div>
  );
}
